import { Directive, Input, Optional, Self } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

/**
 * Gestore personalizzato dello stato di errore per i componenti Angular Material.
 * * Determina quando un campo deve essere visualizzato con lo stile di errore (bordo rosso).
 * A differenza del comportamento standard, permette di forzare lo stato di errore
 * esternamente tramite un booleano.
 * Lo uso principalmente quando non c'è la possibilità di construtire
 * un formbuilder (es 10k campi annidati, altrimenti il DOM esplode) e bisogna mettere un required a livello di template.
 * Questo simuletebbe un touched manuale.
 */
class ForceErrorMatcher implements ErrorStateMatcher {
  /**
   * @param force Se true, ignora se l'utente ha interagito con il campo (touched).
   */
  constructor(private readonly force: boolean) {}

  /**
   * Valuta se il componente deve mostrare l'errore.
   * @param control Il riferimento al controllo associato (NgModel o FormControl).
   * @returns boolean - True se il campo deve diventare rosso.
   */
  isErrorState(control: any): boolean {
    // Il campo è rosso se: è invalido E (è stato toccato dall'utente OPPURE la validazione è forzata)
    return !!(control?.invalid && (control?.touched || this.force));
  }
}

/**
 * Direttiva per forzare la visualizzazione degli errori su MatInput e MatSelect.
 * * Utile quando si vuole validare un form al click di un tasto "Salva", rendendo rossi
 * tutti i campi non compilati anche se l'utente non li ha mai cliccati.
 * * @example
 * ```html
 * <mat-select [appForceErrorView]="mostraErrori" [(ngModel)]="valore" required>
 * </mat-select>
 * ```
 */
@Directive({
  selector: '[appForceErrorView]',
  standalone: true,
})
export class ForceErrorViewDirective {
  /**
   * Input booleano che attiva o disattiva la forzatura visiva dell'errore.
   * Quando il valore cambia, aggiorna il matcher del componente Material ospitante.
   * * @param value Boolean: se true, gli errori appaiono immediatamente se il campo è invalido.
   */
  @Input('appForceErrorView') set force(value: boolean) {
    const matcher = new ForceErrorMatcher(value);

    // Gestione specifica per componenti MatSelect
    if (this.matSelect) {
      this.matSelect.errorStateMatcher = matcher;
      // Triggera manualmente il rilevamento dei cambiamenti per aggiornare la UI
      this.matSelect.stateChanges.next();
    }

    // Gestione specifica per componenti MatInput (input testuali e textarea)
    if (this.matInput) {
      this.matInput.errorStateMatcher = matcher;
      // Triggera manualmente il rilevamento dei cambiamenti per aggiornare la UI
      this.matInput.stateChanges.next();
    }
  }

  /**
   * Inietta i componenti Material se presenti sull'elemento dove è applicata la direttiva.
   * * @param matInput Istanza di MatInput (opzionale).
   * @param matSelect Istanza di MatSelect (opzionale).
   */
  constructor(
    @Optional() @Self() private readonly matInput: MatInput,
    @Optional() @Self() private readonly matSelect: MatSelect,
  ) {}
}
